import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { FullPassOffer } from '../components/FullPassOffer'
import { SpecialEdition2026Offer } from '../components/SpecialEdition2026Offer'
import { PlaceStampImage } from '../components/stamps/PlaceStampImage'
import { decorStampPlaceIds, placeStampById } from '../data/placeStamps'
import { placesMock } from '../data/places'
import { useT } from '../i18n/t'
import { publicCopyKey, FULL_PASS_PUBLIC_ENABLED } from '../lib/productFlags'
import { fadeUp } from '../lib/motion'

const ROTATIONS = [-8, 6, -4, 9, -7, 5] as const

export function LandingStampsShowcase() {
  const { t } = useT()

  const items = decorStampPlaceIds.map((id, idx) => {
    const place = placesMock.find((p) => p.id === id)
    return {
      id,
      src: placeStampById[id]!,
      name: place?.name ?? '',
      region: place?.region ?? '',
      rotation: ROTATIONS[idx] ?? 0,
    }
  })

  return (
    <section
      aria-labelledby="landing-stamps-heading"
      className="relative overflow-hidden border-y border-ink/10 bg-beige/30 dark:bg-beige/10"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.35] dark:hidden"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.7) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.5) 0%, transparent 55%)',
        }}
      />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
        className="container-x relative py-14 md:py-20"
      >
        <motion.div className="content">
          {FULL_PASS_PUBLIC_ENABLED ? (
            <>
              <motion.p
                variants={fadeUp}
                className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/55"
              >
                {t('landing.stamps.eyebrow')}
              </motion.p>
              <motion.h2
                id="landing-stamps-heading"
                variants={fadeUp}
                className="display mt-3 max-w-[28ch] text-[26px] font-bold leading-[1.12] text-ink md:text-[40px]"
              >
                {t('landing.stamps.title')}
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="mt-3 max-w-[58ch] text-[15px] font-medium leading-relaxed text-ink/65 md:text-base"
              >
                {t('landing.stamps.subtitle')}
              </motion.p>
            </>
          ) : (
            <motion.p
              id="landing-stamps-heading"
              variants={fadeUp}
              className="max-w-[58ch] text-[15px] font-medium leading-relaxed text-ink/65 md:text-[17px]"
            >
              {t('landing.stamps.descMiniOnly.before')}
              <Link
                to="/spolecznosc"
                className="font-semibold text-ink underline decoration-ink/25 underline-offset-4 transition hover:decoration-ink/55"
              >
                {t('landing.stamps.descMiniOnly.communityLink')}
              </Link>
              {t('landing.stamps.descMiniOnly.after')}
            </motion.p>
          )}

          {FULL_PASS_PUBLIC_ENABLED ? (
            <motion.div variants={fadeUp} className="mt-8">
              <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
                <FullPassOffer />
                <SpecialEdition2026Offer mode="landing" />
              </div>
            </motion.div>
          ) : null}

          <motion.ul
            variants={fadeUp}
            className={`grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:grid-cols-6 md:gap-x-6 ${FULL_PASS_PUBLIC_ENABLED ? 'mt-10 md:mt-12' : 'mt-10'}`}
            aria-label={t('landing.stamps.listAria')}
          >
            {items.map((item) => (
              <li key={item.id} className="flex flex-col items-center text-center">
                <PlaceStampImage
                  src={item.src}
                  alt={item.name}
                  width={150}
                  height={150}
                  rotation={item.rotation}
                  className="h-[120px] w-[120px] md:h-[150px] md:w-[150px]"
                  imageClassName="h-[88%] w-[88%] object-contain"
                />
                <div className="mt-4 text-[12px] font-extrabold leading-tight text-ink md:text-sm">
                  {item.name}
                </div>
                <div className="mt-0.5 text-[11px] font-semibold text-ink/55 md:text-xs">
                  {item.region}
                </div>
              </li>
            ))}
          </motion.ul>

          <motion.p
            variants={fadeUp}
            className="mt-10 text-[13px] font-semibold text-ink/55 md:text-sm"
          >
            {t(publicCopyKey('landing.stamps.footnote', 'landing.stamps.footnoteMiniOnly'))}
          </motion.p>
        </motion.div>
      </motion.div>
    </section>
  )
}
